import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Fonts } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

import { MoreTile } from './card-carousel-more-tile';
import type { CardCarouselProps } from './card-carousel.types';
import css from './carousel-section.module.css';

// A presentational horizontal card row: heading + "See all" + scroller with
// desktop arrow buttons. Same markup/CSS as the homepage carousels
// (events-carousel.web.tsx etc.), but the caller passes the cards in instead
// of the component fetching them — used by the city landing page, whose
// events/tours/boats sections used to be fixed-width cards in a wrapping grid
// (one ragged card per row on mobile, a dead 240px strip on desktop).
//
// Every card is still in the DOM (overflow-x, not virtualized), so crawlers
// see the same detail-page links the old grid exposed.
export function CardCarousel({
  heading,
  seeAllHref,
  description,
  moreTile,
  children,
}: CardCarouselProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Each arrow only shows when there's somewhere to scroll in its direction —
  // no "previous" at the start, and none at all for a row that fits. Starts
  // hidden (the static HTML can't know the viewport) and resolves on mount.
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const atStart = el.scrollLeft <= 1;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setEdges((prev) =>
        prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd },
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    // Page by roughly one viewport of cards, leaving one card of overlap.
    el.scrollBy({
      left: dir * Math.max(280, el.clientWidth - 256),
      behavior: 'smooth',
    });
  };

  return (
    <div className={`${css.section} ${css.flush}`}>
      <div className={css.heading}>
        <h2 className={css.headingText} style={{ color: theme.text, fontFamily: Fonts?.serif }}>
          {heading}
        </h2>
        {/* Plain-object style, pressed feedback via children — see the note
            in events-carousel.web.tsx on <Link asChild>'s style merge. */}
        <Link href={seeAllHref} asChild>
          <Pressable accessibilityRole="link" style={styles.seeAll}>
            {({ pressed }) => (
              <>
                <Text
                  style={[
                    styles.seeAllText,
                    { color: theme.primary },
                    pressed && { opacity: 0.6 },
                  ]}>
                  {t('common.seeAll')}
                </Text>
                <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
              </>
            )}
          </Pressable>
        </Link>
      </div>
      {description ? (
        <p className={css.description} style={{ color: theme.text }}>
          {description}
        </p>
      ) : null}
      <div className={css.carouselWrap}>
        <div ref={scrollRef} className={css.scroller}>
          {children}
          {moreTile ? <MoreTile {...moreTile} /> : null}
        </div>
        <div className={css.arrows}>
          {edges.atStart ? null : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.previous')}
              onPress={() => scrollBy(-1)}
              style={({ pressed }) => [
                styles.navBtn,
                styles.navBtnLeft,
                { backgroundColor: theme.card, borderColor: theme.border },
                pressed && { opacity: 0.8 },
              ]}>
              <ChevronLeft size={20} color={theme.text} />
            </Pressable>
          )}
          {edges.atEnd ? null : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.next')}
              onPress={() => scrollBy(1)}
              style={({ pressed }) => [
                styles.navBtn,
                styles.navBtnRight,
                { backgroundColor: theme.card, borderColor: theme.border },
                pressed && { opacity: 0.8 },
              ]}>
              <ChevronRight size={20} color={theme.text} />
            </Pressable>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnLeft: {
    left: -12,
  },
  navBtnRight: {
    right: -12,
  },
});
